import React, { createContext, useContext } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Lock } from "lucide-react";

// ─── MODULE DEFINITIONS ──────────────────────────────────────────────────────
// Each module has a code, which pages it covers, and minimum plan required
export const MODULES = {
  dashboard:            { label: "Dashboard",          page: "Dashboard",             plans: ["basic", "professional", "premium"] },
  time_control:         { label: "Control Horari",     page: "TimeControl",           plans: ["basic", "professional", "premium"] },
  temperatures:         { label: "Temperatures",       page: "Temperatures",          plans: ["basic", "professional", "premium"] },
  chlorine:             { label: "Control de Clor",    page: "ChlorineControl",       plans: ["basic", "professional", "premium"] },
  cleaning:             { label: "Neteja",             page: "Cleaning",              plans: ["basic", "professional", "premium"] },
  incidents:            { label: "Incidències",        page: "Incidents",             plans: ["basic", "professional", "premium"] },
  documents:            { label: "Documents",          page: "Documents",             plans: ["professional", "premium"] },
  downloads:            { label: "Descàrregues",       page: "Downloads",             plans: ["professional", "premium"] },
  alerts:               { label: "Notificacions",      page: "NotificationCenter",    plans: ["professional", "premium"] },
  verifications:        { label: "Verificacions",      page: "Verifications",         plans: ["professional", "premium"] },
  recipe_sheets:        { label: "Fitxes Tècniques",   page: "RecipeSheets",          plans: ["professional", "premium"] },
  ingredients:          { label: "Ingredients",        page: "Ingredients",           plans: ["professional", "premium"] },
  compliance_dashboard: { label: "Compliment",         page: "ComplianceDashboard",   plans: ["professional", "premium"] },
  appcc:                { label: "APPCC",              page: "APPCCManagement",       plans: ["professional", "premium"] },
  rgpd:                 { label: "RGPD",               page: "GDPRManagement",        plans: ["premium"] },
  prl:                  { label: "PRL",                page: "PRLManagement",         plans: ["premium"] },
  harassment:           { label: "Assetjament",        page: "HarassmentProtocol",    plans: ["premium"] },
  analytics:            { label: "Analítica",          page: "VenueAnalytics",        plans: ["premium"] },
  ai_generator:         { label: "Generador IA",       page: "DocumentGenerator",     plans: ["premium"] },
  regulatory:           { label: "Normativa",          page: "RegulatoryCompliance",  plans: ["premium"] },
  technical_compliance: { label: "Seguretat Tècnica",       page: "TechnicalCompliance",  plans: ["professional", "premium"] },
  electrical_compliance:{ label: "Instal·lació Elèctrica", page: "ElectricalCompliance",  plans: ["professional", "premium"] },
  pest_control:         { label: "Control Plagues",    page: "PestControl",           plans: ["professional", "premium"] },
  maintenance:          { label: "Manteniment",        page: "Maintenance",           plans: ["professional", "premium"] },
  traceability:         { label: "Traçabilitat",       page: "Traceability",          plans: ["professional", "premium"] },
  risk_dashboard:       { label: "Tauler de Riscos",   page: "RiskDashboard",         plans: ["professional", "premium"] },
  my_hours:             { label: "Les meves hores",    page: "MyHours",               plans: ["basic", "professional", "premium"] },
  venue_profile:        { label: "Perfil Local",       page: "VenueProfile",          plans: ["basic", "professional", "premium"] },
  team:                 { label: "Equip",              page: "TeamManagement",         plans: ["basic", "professional", "premium"] },
  invoice_scanner:      { label: "Escàner Factures",   page: "InvoiceScanner",        plans: ["professional", "premium"] },
  ai_assistant:         { label: "Assistent IA",       page: "AIAssistant",           plans: ["professional", "premium"] },
  waste_management:     { label: "Malbaratament",      page: "WasteManagement",       plans: ["professional", "premium"] },
};

// ─── ROLE PERMISSIONS ────────────────────────────────────────────────────────
// Each role: { can_view, can_create, can_edit, can_delete, can_export }
export const ROLE_PERMISSIONS = {
  superadmin: { can_view: true, can_create: true, can_edit: true, can_delete: true, can_export: true },
  admin:      { can_view: true, can_create: true, can_edit: true, can_delete: true, can_export: true },
  supervisor: { can_view: true, can_create: true, can_edit: true, can_delete: false, can_export: true },
  worker:     { can_view: true, can_create: true, can_edit: false, can_delete: false, can_export: false },
  auditor:    { can_view: true, can_create: false, can_edit: false, can_delete: false, can_export: true },
};

// ─── PLAN FEATURE MATRIX ─────────────────────────────────────────────────────
export const PLAN_FEATURES = {
  basic:        Object.entries(MODULES).filter(([, m]) => m.plans.includes("basic")).map(([k]) => k),
  professional: Object.entries(MODULES).filter(([, m]) => m.plans.includes("professional")).map(([k]) => k),
  premium:      Object.entries(MODULES).map(([k]) => k),
};

export const PLAN_LABELS = {
  basic: "Bàsic",
  professional: "Professional",
  premium: "Premium",
};

export const PLAN_COLORS = {
  basic:        "bg-gray-100 text-gray-700",
  professional: "bg-purple-100 text-purple-700",
  premium:      "bg-amber-100 text-amber-700",
};

export const STATUS_LABELS = {
  active:    "Actiu",
  trial:     "En prova",
  suspended: "Suspès",
  cancelled: "Cancel·lat",
  expired:   "Expirat",
  inactive:  "Inactiu",
};

export const ROLE_LABELS = {
  superadmin: "Super Admin",
  admin:      "Administrador",
  supervisor: "Supervisor",
  worker:     "Treballador",
  auditor:    "Auditor",
};

// ─── CONTEXT ─────────────────────────────────────────────────────────────────
const PlanContext = createContext({ subscription: null, hasFeature: () => true, can: () => true });
export const PlanContext_ = PlanContext;

export function PlanProvider({ children, subscription, userRole }) {
  const plan = subscription?.plan || "basic";
  const activeSubscription = !subscription ||
    subscription.status === "active" ||
    subscription.status === "trial";

  // Check if plan includes a module
  const hasFeature = (moduleCode) => {
    if (userRole === "superadmin") return true;
    if (!activeSubscription) return moduleCode === "dashboard"; // only dashboard if suspended
    return (PLAN_FEATURES[plan] || PLAN_FEATURES.basic).includes(moduleCode);
  };

  // Check role permission
  const can = (action) => {
    if (!userRole) return false;
    const perms = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.worker;
    return perms[`can_${action}`] === true;
  };

  const subscriptionBlocked = subscription &&
    ["suspended", "cancelled", "expired", "inactive"].includes(subscription.status);

  return (
    <PlanContext.Provider value={{ subscription, hasFeature, can, plan, userRole, subscriptionBlocked }}>
      {children}
    </PlanContext.Provider>
  );
}

export function usePlan() {
  return useContext(PlanContext);
}

// ─── GATE COMPONENTS ─────────────────────────────────────────────────────────

// Wraps content that requires a module/feature
export function FeatureGate({ feature, children, fallback = null }) {
  const { hasFeature } = usePlan();
  if (hasFeature(feature)) return children;
  return fallback;
}

// Wraps content that requires a permission action
export function PermissionGate({ action, children, fallback = null }) {
  const { can } = usePlan();
  if (can(action)) return children;
  return fallback;
}

// Full page guard — shows locked screen if module not available
export function PageGuard({ moduleCode, children }) {
  const { hasFeature, subscriptionBlocked, subscription } = usePlan();

  if (subscriptionBlocked) {
    return <SubscriptionBlockedScreen status={subscription?.status} />;
  }

  if (!hasFeature(moduleCode)) {
    return <ModuleLockedScreen moduleCode={moduleCode} plan={subscription?.plan} />;
  }

  return children;
}

function SubscriptionBlockedScreen({ status }) {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-8">
      <div className="w-16 h-16 rounded-2xl bg-red-100 flex items-center justify-center mb-4">
        <Lock className="w-8 h-8 text-red-500" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Subscripció no activa</h2>
      <p className="text-gray-500 max-w-sm">
        {status === "suspended"
          ? "El vostre compte ha estat suspès temporalment. Contacteu amb AEH Reus per reactivar-lo."
          : status === "expired"
          ? "La vostra subscripció ha expirat. Renoveu-la per continuar usant Kitcha."
          : "La subscripció d'aquest local no està activa. Contacteu amb el vostre administrador."}
      </p>
      <a href="mailto:info@aehreus.cat" className="mt-4 text-sm text-[#7B2D3F] font-medium underline underline-offset-2">
        Contactar AEH Reus
      </a>
    </div>
  );
}

function ModuleLockedScreen({ moduleCode, plan }) {
  const mod = MODULES[moduleCode];
  const nextPlan = plan === "basic" ? "Professional" : "Premium";
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-8">
      <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mb-4">
        <Lock className="w-8 h-8 text-amber-500" />
      </div>
      <h2 className="text-xl font-bold text-gray-900 mb-2">Mòdul no disponible</h2>
      <p className="text-gray-500 max-w-sm mb-4">
        <strong>{mod?.label || moduleCode}</strong> no està inclòs al vostre pla actual.
        Actualitzeu al pla <strong>{nextPlan}</strong> per accedir-hi.
      </p>
      <Link
        to={createPageUrl("VenueProfile")}
        className="inline-flex items-center gap-2 bg-[#7B2D3F] text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-[#5B1D2F] transition-colors"
      >
        Veure plans disponibles
      </Link>
    </div>
  );
}